import { useQuery } from '@tanstack/react-query';
import type { MenuConfig } from '@/services/ConfigService';
import { queryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/hooks/useAuth';
import { DEFAULT_NAVIGATION, applyNavLabelOverrides } from '@/config/defaultNavigation';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';

/**
 * Returns the navigation menu config.
 *
 * The structure comes from the code-level DEFAULT_NAVIGATION constant.
 * Label overrides are read from module.navigation in the DB config,
 * allowing per-deployment customization of menu item labels.
 */
export function useMenuConfig() {
  const { isAuthenticated } = useAuth();
  const { config: navConfig } = useModuleConfig<{ features: { labels: Record<string, string> } }>('navigation');

  return useQuery<MenuConfig | null>({
    queryKey: [...queryKeys.system.uiNav, navConfig],
    queryFn: () => {
      const overrides = navConfig?.features?.labels ?? {};
      return applyNavLabelOverrides(DEFAULT_NAVIGATION, overrides);
    },
    staleTime: Infinity, // structure is static; only labels change
    enabled: isAuthenticated,
  });
}
